import React from 'react';
import { Box, Button, Flex, Text } from '@chakra-ui/react';
import type { SelectedProduct } from '@foodfluence/shared';
import { eyebrowProps, wizardTokens } from '../scheduling/styles';

/**
 * The in-store review step's edit affordance, same shape and wording (board: underlined "Edit").
 */
const EditButton: React.FC<{ label: string; onClick: () => void }> = ({ label, onClick }) => (
  <Button
    variant="unstyled"
    flex="none"
    h="44px"
    minW={0}
    px="4px"
    fontFamily="display"
    fontSize="14px"
    fontWeight={600}
    color={wizardTokens.ink}
    textDecoration="underline"
    aria-label={`Edit ${label}`}
    onClick={onClick}
  >
    Edit
  </Button>
);

const Row: React.FC<{ label: string; value: string; onEdit?: () => void; editLabel?: string }> = ({ label, value, onEdit, editLabel }) => (
  <Flex align="center" gap="10px" py="12px" borderTop="1px solid #ECECEA">
    <Flex flex={1} minW={0} direction="column" gap="3px">
      <Text {...eyebrowProps} lineHeight="normal">{label}</Text>
      <Text fontSize="15px" fontWeight={600} lineHeight="1.35" color={wizardTokens.ink}>{value}</Text>
    </Flex>
    {onEdit && editLabel && <EditButton label={editLabel} onClick={onEdit} />}
  </Flex>
);

interface ProductSelectionSummaryProps {
  products: SelectedProduct[];
  totalUsd: number;
  formatValue: (usd?: number) => string;
  /** Where the products are going — an address to ship to, or a store to collect from. */
  shippingAddress?: string;
  /** Names what that place is. Retail collects in person rather than being shipped to. */
  addressLabel?: string;
  editAddressLabel?: string;
  /** Retail only: when the creator said they would post. */
  postingDate?: string;
  /** Delivery-only UGC (isDeliveryOnlyUgc): label the date as a delivery, not a post. */
  deliveryOnly?: boolean;
  /** Omitted once confirmed, and when the campaign left the creator no choice to change. */
  onEditProducts?: () => void;
  onEditAddress?: () => void;
}

/**
 * What the creator is getting: the products, the total, and where it is going.
 *
 * Shared by the wizard's review step — where the Edit buttons send them back to change it — and
 * the confirmed screen, which renders the same card read-only.
 */
const ProductSelectionSummary: React.FC<ProductSelectionSummaryProps> = ({
  products,
  totalUsd,
  formatValue,
  shippingAddress,
  addressLabel = 'SHIPPING TO',
  editAddressLabel = 'your shipping address',
  postingDate,
  deliveryOnly,
  onEditProducts,
  onEditAddress,
}) => (
  <Box border="1px solid #E4E4E0" borderRadius="18px" px="14px" bg="#FFFFFF" fontFamily="display" color={wizardTokens.ink}>
    <Flex align="center" gap="10px" minH="44px" pt="4px">
      <Flex flex={1} minW={0} direction="column" gap="2px" py="8px">
        <Text {...eyebrowProps} lineHeight="normal">Your selection</Text>
        <Text fontSize="13px" color="#55555A" lineHeight="normal">
          {products.length} item{products.length === 1 ? '' : 's'} on their way to you.
        </Text>
      </Flex>
      {onEditProducts && <EditButton label="your products" onClick={onEditProducts} />}
    </Flex>

    {products.map((product) => (
      <Flex key={product.id} align="center" gap="12px" py="10px" borderTop="1px solid #ECECEA">
        <Box flex={1} minW={0}>
          <Text fontSize="15px" fontWeight={600} lineHeight="1.3" noOfLines={2}>
            {product.name}
          </Text>
          {/* Two picks of the same shirt in different colours would otherwise read alike. */}
          {product.variantName && (
            <Text fontSize="13px" color="#55555A" lineHeight="normal" noOfLines={1}>
              {product.variantName}
            </Text>
          )}
        </Box>
        <Text fontSize="15px" fontWeight={600} flexShrink={0}>
          {formatValue(product.productValue)}
        </Text>
      </Flex>
    ))}

    <Flex align="center" justify="space-between" minH="44px" py="8px" borderTop="1px solid #ECECEA">
      <Text {...eyebrowProps} lineHeight="normal">Total value</Text>
      <Text textStyle="display" fontSize={formatValue(totalUsd).length > 8 ? '22px' : '26px'} lineHeight={1.12} textTransform="none">
        {formatValue(totalUsd)}
      </Text>
    </Flex>

    {shippingAddress && (
      <Row label={addressLabel} value={shippingAddress} onEdit={onEditAddress} editLabel={editAddressLabel} />
    )}

    {postingDate && (
      <Row
        label={deliveryOnly ? 'DELIVERING ON' : 'POSTING ON'}
        value={postingDate}
        onEdit={onEditAddress}
        editLabel={deliveryOnly ? 'your delivery date' : 'your posting date'}
      />
    )}
  </Box>
);

export default ProductSelectionSummary;
